import { CONSTANTS, REVISION } from '../game.js';

export class UIManager {
    constructor(scene) {
        this.scene = scene;
        this.startButton = null;
        this.countdownText = null;
        this.fpsText = null;
        this.timerText = null;
    }
    
    /**
     * 汎用的なボタンを作成する
     * @param {number} y - ボタンのY座標
     * @param {string} text - ボタンのテキスト
     * @param {object} options - ボタンのオプション
     * @param {string} options.backgroundColor - 背景色（デフォルト: '#4CAF50'）
     * @param {string} options.hoverColor - ホバー時の背景色（デフォルト: '#45a049'）
     * @param {string} options.fontSize - フォントサイズ（デフォルト: モバイル'42px'/デスクトップ'36px'）
     * @param {boolean} options.animate - アニメーションを適用するかどうか（デフォルト: true）
     * @param {Function} onClick - クリック時のコールバック関数
     * @returns {Phaser.GameObjects.Text} 作成されたボタン
     */
    createButton(y, text, options = {}, onClick) {
        // デフォルト値の設定
        const isMobile = this.scene.game.device.input.touch;
        const gameWidth = this.scene.game.config.width;
        const centerX = gameWidth / 2;
        
        const defaults = {
            backgroundColor: '#4CAF50',
            hoverColor: '#45a049',
            fontSize: isMobile ? '42px' : '36px',
            fontFamily: 'DotGothic16',
            padding: isMobile ? 
                { left: 20, right: 20, top: 20, bottom: 20 } : 
                { left: 15, right: 15, top: 15, bottom: 15 },
            animate: true,
            animation: {
                scale: 1.05,
                duration: 800,
                ease: 'Sine.easeInOut',
                yoyo: true,
                repeat: -1
            }
        };
        
        // オプションをデフォルト値とマージ
        const settings = { ...defaults, ...options };
        if (options.animation) {
            settings.animation = { ...defaults.animation, ...options.animation };
        }
        
        // ボタンの作成（常に中央に配置）
        const button = this.scene.add.text(
            centerX, y, text,
            {
                fontSize: settings.fontSize,
                fontFamily: settings.fontFamily,
                fill: '#ffffff',
                backgroundColor: settings.backgroundColor,
                padding: settings.padding,
                align: 'center' // テキストを中央揃えに
            }
        );
        button.setOrigin(0.5);
        
        // 固定幅の適用（画面幅の80%）
        const buttonWidth = gameWidth * 0.8;
        
        // 現在のパディングを使用（settings.paddingから）
        const currentPadding = settings.padding;
        
        // テキスト幅を推定（ボタン幅からパディングを引く）
        const textWidth = button.width - (currentPadding.left + currentPadding.right);
        
        // 必要な左右のパディングを計算
        const requiredPadding = Math.max((buttonWidth - textWidth) / 2, currentPadding.left);
        
        // 新しいパディングを設定
        button.setPadding({
            left: requiredPadding,
            right: requiredPadding,
            top: currentPadding.top,
            bottom: currentPadding.bottom
        });
        
        // タッチ領域の設定（ボタンの実際のサイズに基づく）
        const hitAreaPadding = isMobile ? 
            { x: 40, y: 30 } : // モバイル向け（大きめ）
            { x: 20, y: 10 };  // デスクトップ向け
        
        const hitArea = new Phaser.Geom.Rectangle(
            -button.width / 2 - hitAreaPadding.x,
            -button.height / 2 - hitAreaPadding.y,
            button.width + hitAreaPadding.x * 2,
            button.height + hitAreaPadding.y * 2
        );
        button.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
        
        // デスクトップ向けホバー効果
        if (!isMobile) {
            button.on('pointerover', () => {
                button.setStyle({
                    fontFamily: settings.fontFamily,
                    fill: '#ffffff',
                    backgroundColor: settings.hoverColor
                });
            });
            
            button.on('pointerout', () => {
                button.setStyle({
                    fontFamily: settings.fontFamily,
                    fill: '#ffffff',
                    backgroundColor: settings.backgroundColor
                });
            });
        }
        
        // タッチデバイス向けタップフィードバック
        if (isMobile) {
            button.on('pointerdown', () => {
                // タップ時に一時的にボタンを小さくするアニメーション
                this.scene.tweens.add({
                    targets: button,
                    scale: 0.95,
                    duration: 100,
                    yoyo: true,
                    onComplete: () => {
                        if (onClick) onClick();
                    }
                });
            });
        } else {
            // デスクトップ向けクリックイベント
            button.on('pointerdown', () => {
                if (onClick) onClick();
            });
        }
        
        // アニメーションの適用（オプションで制御可能）
        if (settings.animate) {
            this.scene.tweens.add({
                targets: button,
                scale: settings.animation.scale,
                duration: settings.animation.duration,
                ease: settings.animation.ease,
                yoyo: settings.animation.yoyo,
                repeat: settings.animation.repeat
            });
        }
        
        return button;
    }
    
    /**
     * カウントダウンの開始
     * @param {Function} onCompleteCallback - カウントダウン完了時のコールバック
     */
    startCountdown(onCompleteCallback) {
        // カウントダウンテキストの作成
        const fontSize = this.scene.game.device.input.touch ? '96px' : '128px';
        this.countdownText = this.scene.add.text(
            this.scene.game.config.width / 2,
            this.scene.game.config.height / 2,
            '3',
            {
                fontSize: fontSize,
                fontFamily: 'DotGothic16',
                fontStyle: 'bold',
                fill: '#ff0000',
                stroke: '#ffffff',
                strokeThickness: 8
            }
        );
        this.countdownText.setOrigin(0.5);
        this.countdownText.setDepth(30); // 最前面に表示
        
        // カウントダウンアニメーション
        this.scene.tweens.add({
            targets: this.countdownText,
            scale: 1.5,
            duration: 900,
            ease: 'Sine.easeInOut',
            yoyo: true
        });
        
        // 3秒カウントダウン
        this.scene.time.delayedCall(1000, () => {
            if (this.countdownText) {
                this.countdownText.setText('2');
                this.scene.tweens.add({
                    targets: this.countdownText,
                    scale: 1.5,
                    duration: 900,
                    ease: 'Sine.easeInOut',
                    yoyo: true
                });
            }
        });
        
        this.scene.time.delayedCall(2000, () => {
            if (this.countdownText) {
                this.countdownText.setText('1');
                this.scene.tweens.add({
                    targets: this.countdownText,
                    scale: 1.5,
                    duration: 900,
                    ease: 'Sine.easeInOut',
                    yoyo: true
                });
            }
        });
        
        this.scene.time.delayedCall(3000, () => {
            if (this.countdownText) {
                this.countdownText.destroy();
                this.countdownText = null;
                if (onCompleteCallback) onCompleteCallback();
            }
        });
    }


    /**
     * FPSカウンターを作成する
     */
    createFpsCounter() {
        const margin = 0; // 画面端からのマージン
        this.fpsText = this.scene.add.text(margin, this.scene.game.config.height - margin, 'FPS: 0', {
            fontSize: '14px',
            fontFamily: 'DotGothic16',
            fill: '#ffffff',
            backgroundColor: '#333333',
            padding: { x: 8, y: 4 },
            alpha: 0.8
        });
        this.fpsText.setOrigin(0, 1); // 左下を基準点に
        this.fpsText.setDepth(100);  // 最前面に表示
    }

    /**
     * FPSカウンターを更新する
     */
    updateFpsCounter() {
        if (this.fpsText) {
            this.fpsText.setText(`FPS: ${Math.round(this.scene.game.loop.actualFps)}`);
        }
    }

    // タイマー関連のメソッドはTimerManagerに移動しました

    /**
     * リビジョン情報を表示するテキストを作成する
     * @returns {Phaser.GameObjects.Text} 作成されたテキストオブジェクト
     */
    createRevisionText() {
        // game.jsからリビジョン情報を取得
        const revision = REVISION;
        const margin = 0; // 画面端からのマージン
        
        this.revisionText = this.scene.add.text(
            this.scene.game.config.width - margin,
            this.scene.game.config.height - margin,
            `Rev: ${revision}`,
            {
                fontSize: '14px',
                fontFamily: 'DotGothic16',
                fill: '#ffffff',
                backgroundColor: '#333333',
                padding: { x: 8, y: 4 },
                alpha: 0.8
            }
        );
        this.revisionText.setOrigin(1, 1); // 右下を基準点に
        this.revisionText.setDepth(100);   // 最前面に表示
        
        return this.revisionText;
    }

    cleanup() {
        if (this.startButton) {
            this.startButton.destroy();
            this.startButton = null;
        }

        if (this.timerText) {
            this.timerText.destroy();
            this.timerText = null;
        }
        
        if (this.countdownText) {
            this.countdownText.destroy();
            this.countdownText = null;
        }

        if (this.fpsText) {
            this.fpsText.destroy();
            this.fpsText = null;
        }
        
        if (this.revisionText) {
            this.revisionText.destroy();
            this.revisionText = null;
        }
    }
}
